import * as React from 'react';
import { useMemo, useState } from 'react';
import { Card, Col, Empty, Row, Space, Tag, Typography, notification } from 'antd';
import {
    DndContext,
    DragEndEvent,
    DragOverlay,
    DragStartEvent,
    PointerSensor,
    useDraggable,
    useDroppable,
    useSensor,
    useSensors
} from '@dnd-kit/core';
import { useNavigate } from 'react-router-dom';
import type { Campaign } from './types';
import { useUpdateCampaign } from './queryHooks';

type Status = 'spec' | 'active' | 'completed' | 'canceled';

const COLUMNS: { status: Status; title: string; color: string }[] = [
    { status: 'spec', title: 'Spec', color: '#faad14' },
    { status: 'active', title: 'Active', color: '#52c41a' },
    { status: 'completed', title: 'Completed', color: '#1677ff' },
    { status: 'canceled', title: 'Canceled', color: '#ff4d4f' }
];

type Props = {
    campaigns: Campaign[];
};

const CampaignsBoard: React.FC<Props> = ({ campaigns }) => {
    const navigate = useNavigate();
    const update = useUpdateCampaign();
    const sensors = useSensors(
        useSensor(PointerSensor, {
            // Require a small drag distance before initiating — lets clicks
            // on the card title still navigate to the view page.
            activationConstraint: { distance: 5 }
        })
    );

    const [draggingId, setDraggingId] = useState<number | null>(null);

    const byStatus = useMemo(() => {
        const groups: Record<Status, Campaign[]> = {
            spec: [],
            active: [],
            completed: [],
            canceled: []
        };
        for (const c of campaigns) {
            const s = (c.status as Status) || 'spec';
            if (groups[s]) groups[s].push(c);
            else groups.spec.push(c);
        }
        return groups;
    }, [campaigns]);

    const draggingCampaign = useMemo(
        () => campaigns.find((c) => c.id === draggingId) ?? null,
        [campaigns, draggingId]
    );

    const handleDragStart = (e: DragStartEvent) => {
        setDraggingId(Number(e.active.id));
    };

    const handleDragEnd = (e: DragEndEvent) => {
        setDraggingId(null);
        const overStatus = e.over?.data.current?.status as Status | undefined;
        const campaign = e.active.data.current?.campaign as Campaign | undefined;
        if (!overStatus || !campaign || !campaign.id) return;
        if (campaign.status === overStatus) return;

        // Status changes omit creators. The update handler keeps existing links
        // when that key is absent, so a drag does not unlink the roster.
        update.mutate(
            {
                id: campaign.id,
                name: campaign.name,
                project: campaign.project,
                notes: campaign.notes,
                brief: campaign.brief,
                budget: campaign.budget,
                deliverables: campaign.deliverables,
                brand_id: campaign.brand_id,
                status: overStatus,
                start_date: campaign.start_date,
                end_date: campaign.end_date,
            },
            {
                onSuccess: () =>
                    notification.success({
                        message: `Moved "${campaign.name}" to ${overStatus}`,
                        placement: 'bottomRight',
                        duration: 2
                    }),
                onError: () =>
                    notification.error({
                        message: 'Failed to update status',
                        placement: 'bottomRight'
                    })
            }
        );
    };

    return (
        <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            <Row gutter={12} style={{ alignItems: 'flex-start' }}>
                {COLUMNS.map((col) => (
                    <Col key={col.status} span={6}>
                        <BoardColumn
                            title={col.title}
                            status={col.status}
                            accent={col.color}
                            campaigns={byStatus[col.status]}
                            onCardClick={(id) => navigate(`/creators/campaigns/${id}`)}
                        />
                    </Col>
                ))}
            </Row>
            <DragOverlay>
                {draggingCampaign ? (
                    <CampaignCard campaign={draggingCampaign} onClick={() => {}} isOverlay />
                ) : null}
            </DragOverlay>
        </DndContext>
    );
};

const BoardColumn: React.FC<{
    title: string;
    status: Status;
    accent: string;
    campaigns: Campaign[];
    onCardClick: (id: number) => void;
}> = ({ title, status, accent, campaigns, onCardClick }) => {
    const { setNodeRef, isOver } = useDroppable({
        id: `col-${status}`,
        data: { status }
    });

    return (
        <div
            ref={setNodeRef}
            style={{
                background: isOver ? '#f0f5ff' : 'var(--bg-light, #fafafa)',
                border: `1px solid ${isOver ? '#91caff' : 'var(--border-light, #e0e0e0)'}`,
                borderTop: `3px solid ${accent}`,
                borderRadius: 6,
                padding: 12,
                minHeight: 320,
                transition: 'background 120ms, border-color 120ms'
            }}
        >
            <Space style={{ marginBottom: 12, justifyContent: 'space-between', width: '100%' }}>
                <Typography.Text strong>{title}</Typography.Text>
                <Tag style={{ margin: 0 }}>{campaigns.length}</Tag>
            </Space>
            {campaigns.length === 0 ? (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={false}
                    style={{ margin: '24px 0', opacity: 0.5 }}
                />
            ) : (
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    {campaigns.map((c) => (
                        <DraggableCampaignCard
                            key={c.id}
                            campaign={c}
                            onClick={() => onCardClick(c.id!)}
                        />
                    ))}
                </Space>
            )}
        </div>
    );
};

const DraggableCampaignCard: React.FC<{ campaign: Campaign; onClick: () => void }> = ({
    campaign,
    onClick
}) => {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: campaign.id!,
        data: { campaign }
    });

    return (
        <div
            ref={setNodeRef}
            {...listeners}
            {...attributes}
            style={{ opacity: isDragging ? 0.4 : 1, cursor: 'grab' }}
        >
            <CampaignCard campaign={campaign} onClick={onClick} />
        </div>
    );
};

const CampaignCard: React.FC<{
    campaign: Campaign;
    onClick: () => void;
    isOverlay?: boolean;
}> = ({ campaign, onClick, isOverlay }) => {
    const creatorCount = campaign.creator_count ?? 0;

    return (
        <Card
            size="small"
            hoverable
            onClick={onClick}
            style={{
                cursor: isOverlay ? 'grabbing' : 'pointer',
                boxShadow: isOverlay ? '0 6px 16px rgba(0,0,0,0.15)' : undefined
            }}
        >
            <Typography.Text strong>{campaign.name}</Typography.Text>
            {campaign.brand?.name && (
                <div style={{ marginTop: 6 }}>
                    <Tag color="geekblue" style={{ margin: 0 }}>{campaign.brand.name}</Tag>
                </div>
            )}
            {campaign.budget != null && (
                <div style={{ marginTop: 4, color: '#555', fontSize: 12 }}>
                    €{new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(campaign.budget)}
                </div>
            )}
            {(campaign.start_date || campaign.end_date) && (
                <div style={{ marginTop: 4, color: '#888', fontSize: 11 }}>
                    {campaign.start_date ? new Date(campaign.start_date).toLocaleDateString() : '?'}
                    {' → '}
                    {campaign.end_date ? new Date(campaign.end_date).toLocaleDateString() : '?'}
                </div>
            )}
            <div style={{ marginTop: 6, color: '#888', fontSize: 12 }}>
                {creatorCount} creator{creatorCount === 1 ? '' : 's'}
            </div>
        </Card>
    );
};

export default CampaignsBoard;
